/**
 * Lokální databáze v IndexedDB – tenká vrstva bez externích knihoven.
 *
 * - `tx()` spustí funkci v jedné transakci (vše, nebo nic),
 * - `subscribe()` dává vědět o změnách (i z jiné záložky přes BroadcastChannel),
 *   na tom stojí hook `useQuery` v UI.
 */
import {
  BACKUP_FORMAT,
  SCHEMA_VERSION,
  type Apiary,
  type Backup,
  type Colony,
  type ColonyEvent,
  type Product,
  type Queen,
  type Setting,
} from './model';
import { newEntity, nowISO, splitPerColony } from './rules';
import { backupProblem } from './backup-check';

export interface StoreMap {
  apiaries: Apiary;
  colonies: Colony;
  queens: Queen;
  events: ColonyEvent;
  products: Product;
  settings: Setting;
}
export type StoreName = keyof StoreMap;
export const STORES: StoreName[] = ['apiaries', 'colonies', 'queens', 'events', 'products', 'settings'];

const DEFAULT_DB_NAME = 'vcelarska-aplikace';

// ---------------------------------------------------------------------------
// Otevření a schéma
// ---------------------------------------------------------------------------

let dbName = DEFAULT_DB_NAME;
let dbPromise: Promise<IDBDatabase> | null = null;

/** Pro testy: použít jinou databázi. */
export function useDatabaseName(name: string) {
  dbName = name;
  dbPromise = null;
}

export function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, SCHEMA_VERSION);
    req.onupgradeneeded = (ev) => {
      const db = req.result;
      const tx = req.transaction!;
      if (ev.oldVersion < 1) {
        db.createObjectStore('apiaries', { keyPath: 'id' });
        const colonies = db.createObjectStore('colonies', { keyPath: 'id' });
        colonies.createIndex('apiaryId', 'apiaryId');
        const queens = db.createObjectStore('queens', { keyPath: 'id' });
        queens.createIndex('colonyId', 'colonyId');
        const events = db.createObjectStore('events', { keyPath: 'id' });
        events.createIndex('colonyIds', 'colonyIds', { multiEntry: true });
        events.createIndex('apiaryId', 'apiaryId');
        events.createIndex('date', 'date');
        events.createIndex('type', 'type');
        db.createObjectStore('products', { keyPath: 'id' });
        db.createObjectStore('settings', { keyPath: 'key' });
        const products = tx.objectStore('products');
        for (const p of defaultProducts()) products.add(p);
      }
      if (ev.oldVersion >= 1 && ev.oldVersion < 2) {
        // Schéma 2: zápis pro více včelstev → samostatný záznam pro každé včelstvo.
        const events = tx.objectStore('events');
        events.openCursor().onsuccess = (e) => {
          const cursor = (e.target as IDBRequest<IDBCursorWithValue | null>).result;
          if (!cursor) return;
          const parts = splitPerColony(cursor.value as ColonyEvent);
          if (parts.length > 1) {
            cursor.update(parts[0]);
            for (const extra of parts.slice(1)) events.put(extra);
          }
          cursor.continue();
        };
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('Databázi blokuje jiná otevřená záložka aplikace.'));
  });
  return dbPromise;
}

export function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

// ---------------------------------------------------------------------------
// Transakce a změny
// ---------------------------------------------------------------------------

export interface Tx {
  get<S extends StoreName>(store: S, key: string): Promise<StoreMap[S] | undefined>;
  all<S extends StoreName>(store: S): Promise<StoreMap[S][]>;
  byIndex<S extends StoreName>(store: S, index: string, value: IDBValidKey): Promise<StoreMap[S][]>;
  put<S extends StoreName>(store: S, value: StoreMap[S]): Promise<void>;
  clear(store: StoreName): Promise<void>;
  remove(store: StoreName, key: string): Promise<void>;
}

const listeners = new Set<() => void>();
const channel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('vcelarska-aplikace') : null;
channel?.addEventListener('message', () => listeners.forEach((l) => l()));
// v Node (testy) nesmí kanál držet proces naživu
(channel as unknown as { unref?: () => void } | null)?.unref?.();

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify() {
  listeners.forEach((l) => l());
  channel?.postMessage('changed');
}

// Zámek zápisů po skončení zkušební doby (nastavuje ho modul licence).
let writeLocked = false;
let lockBypass = false;
export const LOCKED_MESSAGE = 'Zkušební sezóna skončila – nové zápisy jsou možné po aktivaci licence.';
export function setWriteLock(locked: boolean) {
  writeLocked = locked;
}

/** Spustí `fn` v jedné transakci. Uvnitř se smí čekat jen na operace `t`. */
export async function tx<R>(
  stores: StoreName[] | 'all',
  mode: IDBTransactionMode,
  fn: (t: Tx) => Promise<R>,
): Promise<R> {
  const names = stores === 'all' ? STORES : stores;
  if (mode === 'readwrite' && writeLocked && !lockBypass && names.some((n) => n !== 'settings')) throw new Error(LOCKED_MESSAGE);
  const db = await openDB();
  const transaction = db.transaction(names, mode);
  const done = new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error ?? new Error('Transakce zrušena'));
  });
  const t: Tx = {
    get: (s, key) => req(transaction.objectStore(s).get(key)),
    all: (s) => req(transaction.objectStore(s).getAll()),
    byIndex: (s, index, value) => req(transaction.objectStore(s).index(index).getAll(value)),
    put: async (s, value) => {
      await req(transaction.objectStore(s).put(value));
    },
    clear: async (s) => {
      await req(transaction.objectStore(s).clear());
    },
    remove: async (s, key) => {
      await req(transaction.objectStore(s).delete(key));
    },
  };
  let result: R;
  try {
    result = await fn(t);
  } catch (e) {
    done.catch(() => {}); // zrušená transakce už chybu nehlásí – ohlásí se původní chyba
    try {
      transaction.abort();
    } catch {
      /* už ukončená */
    }
    throw e;
  }
  await done;
  if (mode === 'readwrite') notify();
  return result;
}

// Zkratky pro čtení -----------------------------------------------------------

const alive = <T extends { deletedAt?: string | null }>(xs: T[]) => xs.filter((x) => !x.deletedAt);

export const read = {
  get: <S extends StoreName>(s: S, id: string) => tx([s], 'readonly', (t) => t.get(s, id)),
  all: <S extends StoreName>(s: S) =>
    tx([s], 'readonly', (t) => t.all(s)).then((xs) =>
      s === 'settings' ? xs : alive(xs as (StoreMap[S] & { deletedAt?: string | null })[]),
    ),
  byIndex: <S extends StoreName>(s: S, index: string, value: IDBValidKey) =>
    tx([s], 'readonly', (t) => t.byIndex(s, index, value)).then((xs) =>
      alive(xs as (StoreMap[S] & { deletedAt?: string | null })[]),
    ),
};

export async function getSetting<T>(key: string): Promise<T | undefined> {
  const s = await tx(['settings'], 'readonly', (t) => t.get('settings', key));
  return s?.value as T | undefined;
}

export function setSetting<T>(key: string, value: T) {
  return tx(['settings'], 'readwrite', (t) => t.put('settings', { key, value }));
}

// ---------------------------------------------------------------------------
// Číselník přípravků
// ---------------------------------------------------------------------------

/**
 * Předvyplněné běžné varroacidy. Dávkování a ochranné lhůty se záměrně
 * nevyplňují – uživatel je doplní podle příbalového letáku.
 */
export function defaultProducts(): Product[] {
  const p = (name: string, activeSubstance: string, method: Product['method']): Product =>
    newEntity<Product>({ name, activeSubstance, method, builtin: true, hidden: false });
  return [
    p('Varidol 125 mg/ml', 'amitraz', 'aerosol'),
    p('Gabon PF 90', 'flumethrin', 'strips'),
    p('Formidol 40 ml', 'kyselina mravenčí', 'evaporation'),
    p('Oxuvar 5,7 %', 'kyselina šťavelová', 'trickling'),
    p('Api-Bioxal', 'kyselina šťavelová', 'sublimation'),
    p('Apiguard', 'thymol', 'evaporation'),
    p('Thymovar', 'thymol', 'evaporation'),
  ];
}

// ---------------------------------------------------------------------------
// Záloha a obnova
// ---------------------------------------------------------------------------

export function exportBackup(): Promise<Backup> {
  return tx('all', 'readonly', async (t) => ({
    format: BACKUP_FORMAT,
    schemaVersion: SCHEMA_VERSION,
    exportedAt: nowISO(),
    data: {
      apiaries: await t.all('apiaries'),
      colonies: await t.all('colonies'),
      queens: await t.all('queens'),
      events: await t.all('events'),
      products: await t.all('products'),
      settings: (await t.all('settings')).filter((x) => !DEVICE_SETTINGS.includes(x.key)),
    },
  }));
}

/**
 * Nastavení, která patří k tomuto telefonu a nejdou do zálohy
 * (licence, začátek zkušební doby, klíč autora). Obnova zálohy je zachová.
 */
export const DEVICE_SETTINGS = ['licence', 'trialStart', 'lastSeen', 'licencePrivateKey', 'statsSent', 'pushPrefs'];

/** Nahradí celý obsah databáze zálohou. */
export async function importBackup(backup: Backup): Promise<void> {
  // poškozený nebo cizí soubor se odmítne dřív, než se cokoli smaže
  const problem = backupProblem(backup);
  if (problem) throw new Error(problem);
  lockBypass = true; // obnova zálohy je dovolená i po skončení zkušební doby
  try {
  await tx('all', 'readwrite', async (t) => {
    const keep = (await t.all('settings')).filter((x) => DEVICE_SETTINGS.includes(x.key));
    for (const s of STORES) await t.clear(s);
    for (const x of keep) await t.put('settings', x);
    for (const s of STORES) {
      let rows = (backup.data[s] ?? []) as StoreMap[typeof s][];
      if (s === 'settings') rows = (rows as Setting[]).filter((x) => !DEVICE_SETTINGS.includes(x.key)) as typeof rows;
      if (s === 'events' && backup.schemaVersion < 2) rows = (rows as ColonyEvent[]).flatMap(splitPerColony) as typeof rows;
      for (const row of rows) await t.put(s, row);
    }
    // obnovená data odpovídají stavu zálohy – připomínka zálohy počítá změny až od ní
    await t.put('settings', { key: 'lastBackupAt', value: backup.exportedAt });
  });
  } finally {
    lockBypass = false;
  }
}

// ---------------------------------------------------------------------------
// Koš: smazané záznamy (měkce smazané – deletedAt)
// ---------------------------------------------------------------------------

export type TrashStore = 'apiaries' | 'colonies' | 'events';

export function readTrash() {
  return tx(['apiaries', 'colonies', 'events'], 'readonly', async (t) => {
    const dead = <T extends { deletedAt?: string | null }>(xs: T[]) => xs.filter((x) => !!x.deletedAt);
    return {
      apiaries: dead(await t.all('apiaries')),
      colonies: dead(await t.all('colonies')),
      events: dead(await t.all('events')),
      // pro názvy v seznamu i u smazaných položek
      allApiaries: await t.all('apiaries'),
      allColonies: await t.all('colonies'),
    };
  });
}

/**
 * Trvalé odstranění všeho z koše. Smazané včelstvo nebo stanoviště, na které
 * se ještě odkazují nesmazané zápisy (např. evidence léčiv), zůstane v koši –
 * jinak by v evidenci chyběl jeho název. Vrací počet ponechaných položek.
 */
export function emptyTrash() {
  return tx(['apiaries', 'colonies', 'events'], 'readwrite', async (t) => {
    const events = await t.all('events');
    for (const e of events) if (e.deletedAt) await t.remove('events', e.id);
    const liveEvents = events.filter((e) => !e.deletedAt);
    const colonies = await t.all('colonies');
    let kept = 0;
    const keptColonies = new Set<string>();
    for (const c of colonies) {
      if (!c.deletedAt) continue;
      if (liveEvents.some((e) => e.colonyIds.includes(c.id))) {
        kept++;
        keptColonies.add(c.id);
      } else await t.remove('colonies', c.id);
    }
    for (const a of await t.all('apiaries')) {
      if (!a.deletedAt) continue;
      const used =
        liveEvents.some((e) => e.apiaryId === a.id) || colonies.some((c) => c.apiaryId === a.id && (!c.deletedAt || keptColonies.has(c.id)));
      if (used) kept++;
      else await t.remove('apiaries', a.id);
    }
    return kept;
  });
}

/** Kolik záznamů přibylo nebo se změnilo od daného okamžiku (pro připomínku zálohy). */
export function changesSince(iso: string | undefined) {
  return tx(['apiaries', 'colonies', 'queens', 'events'], 'readonly', async (t) => {
    let n = 0;
    for (const s of ['apiaries', 'colonies', 'queens', 'events'] as const)
      for (const x of await t.all(s)) if (!iso || x.updatedAt > iso) n++;
    return n;
  });
}
